import * as gl from 'glance-datatypes';

import {
  Partner,
  Region,
  TargetDeviceProperties,
  RuleIds,
} from 'glance-datatypes';

import Utils from './Utils';
import { Emoji, EmojiMapping, EmojiUploadError } from './pages/drafts/types';

export default class RestService {
  static authClient = new gl.AuthServiceClient(
    new gl.Thrift.Protocol(new gl.Thrift.Transport('/auth')),
  );
  static partnerClient = new gl.PartnerServiceClient(
    new gl.Thrift.Protocol(new gl.Thrift.Transport('/api/glance/partner')),
  );
  static draftClient = new gl.GlanceDraftServiceClient(
    new gl.Thrift.Protocol(new gl.Thrift.Transport('/api/glance/draft')),
  );
  static confClient = new gl.GlanceConfigServiceClient(
    new gl.Thrift.Protocol(new gl.Thrift.Transport('/api/config')),
  );
  static dataClient = new gl.GlanceDataServiceClient(
    new gl.Thrift.Protocol(new gl.Thrift.Transport('/api/glance/data')),
  );

  static getStudioAppConfig(callback: (callback: gl.StudioAppConfig) => any) {
    RestService.confClient
      .getStudioAppConfig(function() {})
      .done(function(result: gl.StudioAppConfig) {
        // console.log(result)
        callback(result);
      })
      .fail(function(result: any) {
        console.error(result);
        callback(null);
      });
  }

  static logout(): boolean {
    return RestService.authClient.logout();
  }

  static loginUsingEmailAndPassword(
    username: string,
    password: string,
    callback: (data: any) => any,
  ) {
    RestService.authClient
      .loginUsingEmailAndPassword(username, password, function() {})
      .done(function(result: any) {
        // console.log(result)
        callback(result);
      })
      .fail(function(result: any) {
        console.error(result);
        callback(null);
      });
  }

  static loginUsingMicrosoftAuth(token: string, callback: (data: any) => any) {
    RestService.authClient
      .loginUsingMicrosoft(token, function() {})
      .done(function(result: any) {
        callback(result);
      })
      .fail(function(result: any) {
        console.error(result);
        callback(null);
      });
  }

  static loginUsingGoogleAuth(token: string, callback: (data: any) => any) {
    RestService.authClient
      .loginUsingGoogleAuth(token, function() {})
      .done(function(result: any) {
        // console.log(result)
        callback(result);
      })
      .fail(function(result: any) {
        console.error(result);
        callback(null);
      });
  }

  static getPartnerPublishedGlances(payload) {
    return new Promise((resolve, reject) => {
      const {
        createdAfterSeconds,
        limit,
        offset,
        type,
        state,
        partner,
        region,
        search,
        expired,
        languages,
      } = payload;
      const pagination = {
        limit,
        offset,
        search: search,
      };
      RestService.partnerClient
        .search(
          new gl.PartnerPairSearchQuery({
            partner,
            region,
            state,
            glanceType: type,
            createdAfterInSecs: createdAfterSeconds,
            expired,
            pagination,
            languages,
          }),
          function() {},
        )
        .done(function(result: any) {
          resolve(result);
        })
        .fail(function(result: any) {
          RestService.handlerError(result);
          reject(result);
        });
    });
  }

  static getGlances(payload) {
    return new Promise((resolve, reject) => {
      const {
        createdAfterSeconds,
        limit,
        offset,
        type,
        state,
        partner,
        region,
        search,
        expired,
      } = payload;
      const pagination = {
        limit,
        offset,
        search: search,
      };
      RestService.dataClient
        .search(
          new gl.GlancePairSearchQuery({
            partner,
            region,
            state,
            glanceType: type,
            createdAfterInSecs: createdAfterSeconds,
            expired,
            pagination,
          }),
          function() {},
        )
        .done(function(result: any) {
          console.log(result);
          resolve(result);
        })
        .fail(function(result: any) {
          console.error(result);
          RestService.handlerError(result);
          reject(result);
        });
    });
  }

  static getLiveGlancesBetweenTime(
    partner: gl.Partner,
    region: gl.Region,
    startAtGreaterThan: gl.RelativeTime,
    startAtLessThan: gl.RelativeTime,
    callback: (data: gl.GlancePair[]) => void,
  ) {
    RestService.dataClient
      .search(
        new gl.GlancePairSearchQuery({
          partner: partner,
          region: region,
          startsAt: startAtGreaterThan,
          endsAt: startAtLessThan,
        }),
        function() {},
      )
      .done(function(result: any) {
        console.log(result);
        callback(result.pairs);
      })
      .fail(function(result: any) {
        console.error(result);
        RestService.handlerError(result);
        callback(null);
      });
  }

  static removePartnerPublishedGlance(
    partner: gl.Partner,
    region: gl.Region,
    id: string,
    callback: (success: boolean) => any,
  ) {
    RestService.dataClient
      .removeMeta(partner, region, id, function() {})
      .done(function(result: any) {
        callback(true);
      })
      .fail(function(result: any) {
        console.error(result);
        callback(false);
      });
  }

  static removePartnerPublishedGlances(
    partner: gl.Partner,
    region: gl.Region,
    ids: string[],
    callback: (success: boolean) => any,
  ) {
    RestService.dataClient
      .removeMetas(partner, region, ids, function() {})
      .done(function(result: any) {
        callback(true);
      })
      .fail(function(result: any) {
        console.error(result);
        callback(false);
      });
  }

  static getPublishedGlances(
    region: gl.Region,
    partner: gl.Partner,
    callback: (data: gl.GlancePair[]) => void,
  ) {
    RestService.partnerClient
      .search(
        new gl.PartnerPairSearchQuery({
          region: region,
          partner: partner,
        }),
        function() {},
      )
      .done(function(result: any) {
        callback(result.pairs);
      })
      .fail(function(result: any) {
        console.error(result);
        // RestService.handlerError(result)
        callback([]);
      });
  }

  static removeGlance(
    region: gl.Region,
    id: string,
    partner: gl.Partner,
    callback: (success: boolean) => any,
  ) {
    RestService.partnerClient
      .removeMeta(region, id, partner, function() {})
      .done(function(result: any) {
        callback(true);
      })
      .fail(function(result: any) {
        console.error(result);
        // RestService.handlerError(result)
        callback(false);
      });
  }

  static removeGlances(
    region: gl.Region,
    ids: string[],
    partner: gl.Partner,
    callback: (success: boolean) => any,
  ) {
    console.log('removeGlances', region, ids);
    RestService.partnerClient
      .removeMetas(region, ids, partner, function() {})
      .done(function(result: any) {
        console.log('removeGlances', true);
        callback(true);
      })
      .fail(function(result: any) {
        console.error(result);
        // RestService.handlerError(result)
        callback(false);
      });
  }

  static approveGlance(
    region: gl.Region,
    id: string,
    partner: gl.Partner,
    callback: (data: gl.GlanceData) => any,
  ) {
    RestService.partnerClient
      .approveGlance(region, id, partner, function() {})
      .done(function(result: any) {
        callback(result.data);
      })
      .fail(function(result: any) {
        console.error(result);
        // RestService.handlerError(result)
        callback(null);
      });
  }

  static approveGlances(
    region: gl.Region,
    ids: string[],
    partner: gl.Partner,
    callback: (result: boolean) => any,
  ) {
    RestService.partnerClient
      .approveGlances(region, ids, partner, function() {})
      .done(function(result: any) {
        callback(true);
      })
      .fail(function(result: any) {
        console.error(result);
        // RestService.handlerError(result)
        callback(false);
      });
  }

  static rejectGlance(
    region: gl.Region,
    id: string,
    partner: gl.Partner,
    callback: (data: gl.GlanceData) => any,
  ) {
    RestService.partnerClient
      .rejectGlance(region, id, partner, function() {})
      .done(function(result: any) {
        callback(result.data);
      })
      .fail(function(result: any) {
        console.error(result);
        // RestService.handlerError(result)
        callback(null);
      });
  }

  static rejectGlances(
    region: gl.Region,
    ids: string[],
    partner: gl.Partner,
    callback: (result: boolean) => any,
  ) {
    RestService.partnerClient
      .rejectGlances(region, ids, partner, function() {})
      .done(function(result: any) {
        callback(true);
      })
      .fail(function(result: any) {
        console.error(result);
        callback(false);
      });
  }

  static createDraftFromLink(
    link: string,
    cardType: number,
    testMode: boolean,
    callback: (draft: gl.GlanceDraft) => any,
  ) {
    RestService.draftClient
      .createFromUrl(
        link,
        new gl.TargetDeviceProperties({
          targetDevice:
            cardType == 1 ? gl.TargetDevice.MOBILE : gl.TargetDevice.ANDROID_TV,
        }),
        testMode,
        function() {},
      )
      .done(function(result: any) {
        callback(result.draft);
      })
      .fail(function(result: any) {
        console.error(result);
        RestService.handlerError(result);
        callback(null);
      });
  }

  static createDraftFromImage(
    imageId: string,
    cardType: number,
    callback: (draft: gl.GlanceDraft) => any,
  ) {
    RestService.draftClient
      .createFromImage(imageId, function() {})
      .done(function(result: any) {
        callback(result.draft);
      })
      .fail(function(result: any) {
        console.error(result);
        RestService.handlerError(result);
        callback(null);
      });
  }

  static getAllDrafts(payload): any {
    return new Promise((resolve, reject) => {
      const {
        createdAfterSeconds,
        categoryIds,
        offset,
        limit,
        type,
        storyType,
        publisherId,
      } = payload;
      const pagination = {
        offset,
        limit,
        search: '',
      };
      const glanceType = type;
      RestService.draftClient
        .searchDrafts(
          new gl.GlanceDraftSearchQuery({
            createdAfterInSecs: createdAfterSeconds,
            pagination,
            glanceType,
            categoryIds,
            publisherId,
            storyType,
          }),
          function() {},
        )
        .done(function(result: any) {
          console.log(result);
          resolve(result);
        })
        .fail(function(result: any) {
          console.error(result);
          reject(result);
        });
    });
  }

  static getDraftsByIds(
    draftIds: string[],
    callback: (drafts: gl.GlanceDraft[]) => any,
  ): void {
    RestService.draftClient
      .searchDrafts(
        new gl.GlanceDraftSearchQuery({
          draftIds: draftIds,
        }),
        function() {},
      )
      .done(function(result: any) {
        console.log(result);
        callback(result.drafts);
      })
      .fail(function(result: any) {
        console.error(result);
        callback(null);
      });
  }

  static async checkDraftById(draftId:string):Promise<boolean>{
    const responsePromise = new Promise<gl.GlanceDraftResponse>((resolve)=>{
        RestService.draftClient.get(draftId, resolve)
    })
    const timerPromise = new Promise<never>((_, rej) => setTimeout(()=>rej('timeOutError'), 5000));
    return Promise.race([responsePromise, timerPromise]).then((res)=>{
        return res.draft ===null;
    })
 }

  static getDraftsByPartnerAndRegion(payload): any {
    return new Promise((resolve, reject) => {
      const {
        partner,
        createdAfterSeconds,
        region,
        offset,
        limit,
        type,
        isPublished,
        categoryIds,
        storyType,
      } = payload;
      const pagination = {
        offset,
        limit,
        search: '',
      };
      RestService.draftClient
        .getMetasForDrafts(
          new gl.GlanceDraftMetaSearchQuery({
            partner,
            region,
            createdAfterInSecs: createdAfterSeconds,
            pagination,
            glanceType: type,
            publishedOnly: isPublished,
            categoryIds,
            storyType,
          }),
          function() {},
        )
        .done(function(result: any) {
          console.log(result);
          resolve(result);
        })
        .fail(function(result: any) {
          console.error(result);
          reject(null);
        });
    });
  }

  static saveDraft(draft: gl.GlanceDraft, callback?: (draft: any) => any) {
    RestService.draftClient
      .update(draft, function() {})
      .done(function(result: any) {
        // console.log(result.draft)
        if (callback) {
          callback(result.draft);
        }
      })
      .fail(function(result: any) {
        console.error(result);
        RestService.handlerError(result);
        if (callback) {
          callback(null);
        }
      });
  }

  static overrideDraft(draft: gl.GlanceDraft, callback?: (error: any) => any) {
    RestService.dataClient
      .overrideData(draft, function() {})
      .done(function(result: any) {
        console.log(result);
        if (callback) {
          callback(null);
        }
      })
      .fail(function(result: any) {
        console.error(result);
        RestService.handlerError(result);
        if (callback) {
          callback(result);
        }
      });
  }

  static removeDraft(id: string, callback?: (error: any) => any) {
    RestService.draftClient
      .remove(id, function() {})
      .done(function(result: any) {
        console.log(result);
      })
      .fail(function(result: any) {
        console.error(result);
        RestService.handlerError(result);
        callback(result);
      });
  }

  static glancePauseRequest(
    glanceIds: string[],
    pause: boolean,
    callback: (data) => void,
  ) {
    RestService.dataClient
      .pauseGlance(
        new gl.GlancePauseRequest({
          ids: glanceIds,
          pause,
        }),
        function() {},
      )
      .done(function(result: any) {
        callback(result);
      })
      .fail(function(result: any) {
        console.error(result);
        RestService.handlerError(result);
        callback([]);
      });
  }

  static getPublishMeta(id: string, callback: (metas: gl.GlanceMeta[]) => any) {
    RestService.draftClient
      .getPublishedMeta(id, function() {})
      .done(function(result: any) {
        console.log(result);
        callback(result.metas);
      })
      .fail(function(result: any) {
        console.error(result);
        // RestService.handlerError(result)
        callback(null);
      });
  }

  static getDraftToPublishMeta(
    ids: string[],
    callback: (draftToMetaMap: Map<string, gl.GlanceMeta[]>) => any,
  ) {
    RestService.draftClient
      .getPublishedMetaForDrafts(ids, function() {})
      .done(function(result: any) {
        // console.log(result)
        callback(result.draftToMetaMap);
      })
      .fail(function(result: any) {
        console.error(result);
        // RestService.handlerError(result)
        callback(null);
      });
  }

  static publishDraft(
    draftId: string,
    region: gl.Region,
    partners: gl.Partner[],
    glanceTime: gl.GlanceTime,
    experiments: gl.ExperimentMeta[],
    segmentRulesList = [],
    isPartedGlance: boolean,
    partingSchedule,
    callback?: (result: any, error: any) => any,
  ) {
    const regions: gl.RegionRequest[] = [];
    const ruleIdsList: RuleIds[] = [];
    segmentRulesList
      .map((segment) => segment !== null && segment.map((s) => s.ruleId))
      .filter((ele) => ele !== null)
      .map((rules) => ruleIdsList.push({ ruleIdlist: rules }));
    partners.forEach((partner) => {
      regions.push(
        new gl.RegionRequest({
          partner: partner,
          region: region,
          glanceTime: glanceTime,
          experiments: experiments,
          ruleIds: [],
          ruleIdsList: ruleIdsList,
          isPartedGlance: isPartedGlance,
          partingSchedule: partingSchedule,
        }),
      );
    });
    RestService.dataClient
      .publish(
        new gl.PublishRequest({
          draftId: draftId,
          regions: regions,
        }),
        function() {},
      )
      .done(function(result: any) {
        console.log(result);
        callback(result, null);
      })
      .fail(function(result: any) {
        console.error(result);
        RestService.handlerError(result);
        callback(null, result);
      });
  }

  static async uploadImage(image: any, callback?: (image: gl.Image) => any) {
    console.log(image);
    const fd = new FormData();
    fd.append('file', image);
    let result;
    try {
      result = await $.ajax({
        type: 'POST',
        url: '/upload',
        contentType: false,
        processData: false,
        data: fd,
      });
      let image = result as gl.Image;
      callback(image);
    } catch (error) {
      console.error(error);
      RestService.handlerError(error);
      callback(null);
    }
  }

  static async uploadBubbleImage(image: any) {
    const data = new FormData();
    data.append('file', image);
    try {
      const response = await $.ajax({
        method: 'POST',
        url: `/bubbleUpload`,
        contentType: false,
        processData: false,
        data,
        dataType: 'text',
      });
      return response;
    } catch (error) {
      console.error(error);
    }
  }

  static async uploadCsv(csv: any) {
    console.log(csv);
    const fd = new FormData();
    fd.append('file', csv);
    try {
      await $.ajax({
        type: 'POST',
        url: '/uploadCsv',
        contentType: false,
        processData: false,
        data: fd,
      });
    } catch (error) {
      console.error(error);
      RestService.handlerError(error);
    }
  }

  static getSegments(callback?: (segmentTable: any[]) => any) {
    $.ajax({
      type: 'GET',
      url: '/rest/api/v0/segment/getall',
      contentType: false,
      processData: false,
      success: function(dataResponse) {
        callback(dataResponse);
      },
      error: function(response) {
        console.error(response);
        RestService.handlerError(response);
        callback(null);
      },
    });
  }

  static addSegment(newSegment: any, callback?: (response: any) => void) {
    $.ajax({
      type: 'POST',
      dataType: 'json',
      contentType: 'application/json',
      url: '/rest/api/v0/segment/add',
      data: JSON.stringify(newSegment),
      success: function(dataResponse) {
        callback(dataResponse);
      },
      error: function(response) {
        RestService.handlerError(response);
        console.error(response);
      },
    });
  }

  static getBubbles(callback?: (segmentTable: any[]) => any) {
    $.ajax({
      type: 'GET',
      url: '/rest/api/v0/bubble/getBubble/',
      contentType: false,
      processData: false,
      success: function(dataResponse) {
        callback(dataResponse);
      },
      error: function(response) {
        console.error(response);
        RestService.handlerError(response);
        callback(null);
      },
    });
  }

  static addUpdateBubble(bubbleData: any, callback?: (response: any) => void) {
    $.ajax({
      type: 'POST',
      dataType: 'json',
      contentType: 'application/json',
      url: '/rest/api/v0/bubble/updateBubble/ ',
      data: JSON.stringify(bubbleData),
      success: function(dataResponse) {
        callback(dataResponse);
      },
      error: function(response) {
        RestService.handlerError(response);
        console.error(response);
      },
    });
  }

  static getEUs(callback?: (response: any) => void) {
    $.ajax({
      type: 'GET',
      contentType: 'application/json',
      url: '/rest/api/v0/javelin/eu',
      success: function(euResponse) {
        callback(euResponse);
      },
      error: function(response) {
        RestService.handlerError(response);
        console.error(response);
      },
    });
  }


  static generateHash(
    string: string,
    callback?: (hash: string, error: any) => any,
  ) {
    RestService.authClient
      .generatePasswordHash(string, function() {})
      .done(function(result: any) {
        console.log(result);
        callback(result, null);
      })
      .fail(function(result: any) {
        console.error(result);
        RestService.handlerError(result);
        callback(null, result);
      });
  }

  static handlerError(result: any) {
    Utils.openNewTab(
      '/#/error?html=' + encodeURIComponent(result.responseText),
    );
  }

  static getEmojis(auth: string, callback: (data: Emoji[]) => void) {
    $.ajax({
      type: 'GET',
      url: `${Utils.getContextualEmojiUrl()}/api/v1/emojis`,
      headers: {
        Authorization: auth,
      },
      contentType: false,
      processData: false,
      success: function(dataResponse) {
        callback(dataResponse);
      },
      error: function(error) {
        RestService.handlerError(error);
        callback(null);
      },
    });
  }

  static getDraftEmojiMapping(
    auth: string,
    draftId: string,
    callback: (data: EmojiMapping) => void,
  ) {
    $.ajax({
      type: 'GET',
      url: `${Utils.getContextualEmojiUrl()}/api/v1/glance-emojis?glanceIds=${draftId}`,
      headers: {
        Authorization: auth,
      },
      contentType: false,
      processData: false,
      success: function(dataResponse) {
        callback(dataResponse);
      },
      error: function(error) {
        RestService.handlerError(error);
        callback(null);
      },
    });
  }

  static submitEmoji(
    auth: string,
    payload: any,
    callback: (data: any) => void,
  ) {
    console.log('Rest service', payload);
    $.ajax({
      type: 'POST',
      url: `${Utils.getContextualEmojiUrl()}/api/v1/glance-emojis`,
      headers: {
        Authorization: auth,
      },
      contentType: 'application/json',
      processData: false,
      data: JSON.stringify(payload),
      success: function(dataResponse) {
        callback(dataResponse);
      },
      error: function(response) {
        RestService.handlerError(response);
        console.error(response);
      },
    });
  }

  static uploadEmoji(
    auth: string,
    emojis: any,
    callback: (data: Emoji[]) => void,
    errorCallback: (data: any) => void,
  ) {
    $.ajax({
      type: 'POST',
      url: `${Utils.getContextualEmojiUrl()}/api/v1/emojis`,
      headers: {
        Authorization: auth,
      },
      contentType: false,
      processData: false,
      data: emojis,
      success: function(dataResponse) {
        callback(dataResponse);
      },
      error: function(response) {
        errorCallback(JSON.parse(response.responseText));
      },
    });
  }

  static getCDNData(callback: any, url: string) {
    $.ajax({
      type: 'GET',
      url: url,
      contentType: false,
      processData: false,
      success: function(dataResponse) {
        callback(dataResponse);
      },
      error: function(error) {
        callback(null);
      },
    });
  }
}



// WEBPACK FOOTER //
// ./src/RestService.tsx